// tAuto: translation helper with auto-discovery + auto-translation.
// Usage: tAuto('home.hero.title', 'Encontre o lugar perfeito')
// - Returns the translated string when available.
// - Falls back to the Portuguese default and enqueues the key for auto-translation.
// - Translated strings stream back via useDynamicTranslations and i18n re-renders.

import i18n from "./i18n";
import { supabase } from "@/integrations/supabase/client";

const pendingKeys = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(async () => {
    flushTimer = null;
    if (pendingKeys.size === 0) return;
    const keys = Array.from(pendingKeys);
    pendingKeys.clear();
    try {
      await supabase.functions.invoke("auto-translate", { body: { keys } });
    } catch (e) {
      // Cron fallback will pick these up.
      console.warn("[tAuto] auto-translate invoke failed; cron will retry", e);
    }
  }, 1500);
}

function enqueue(key: string, defaultPt: string) {
  pendingKeys.add(key);
  // Insert into translation_queue (idempotent due to unique constraint).
  (supabase as any)
    .from("translation_queue")
    .insert({
      translation_key: key,
      default_text: defaultPt,
      context: "tAuto",
    })
    .then(({ error }: any) => {
      if (error && error.code !== "23505") {
        console.warn("[tAuto] enqueue failed", error);
      }
    });
  scheduleFlush();
}

export function tAuto(
  key: string,
  defaultPt: string,
  vars?: Record<string, string | number>,
): string {
  const exists = i18n.exists(key);
  if (exists) {
    return i18n.t(key, { defaultValue: defaultPt, ...(vars ?? {}) }) as string;
  }
  // Only enqueue once per session per key.
  if (!pendingKeys.has(key)) enqueue(key, defaultPt);
  // Show PT default while translation propagates.
  let out = defaultPt;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, "g"), String(v));
    }
  }
  return out;
}

export default tAuto;
